import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  FolderPlus,
  Inbox,
  MoreHorizontal,
  Pencil,
  Settings,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { AccountAvatar } from "@/features/accounts/AccountAvatar";
import { MailLoadingState } from "@/features/shared/MailLoadingState";
import { MailContextMenu } from "@/features/shared/MailContextMenu";
import { folderIconFor } from "@/lib/folderIcon";
import { useFoldersWithSnapshotUnread } from "@/hooks/useFoldersWithSnapshotUnread";
import { sumInboxUnreadFromFolders } from "@/lib/mail/folderSnapshotUnread";
import { getUnreadHold, subscribeUnreadHold } from "@/lib/mail/mailUnreadHold";
import { buildNavLayoutFromSettings, isDraftsNavFolder, isUserManagedImapFolder, DRAFTS_SAVING_FOLDER_LABEL } from "@/lib/mail/folderNavSections";
import { isOutboxNavFolder } from "@/lib/outbox/constants";
import {
  accountOutboxTotalCount,
  resolveOutboxNavLabel,
  type AccountOutboxState,
} from "@/lib/outbox/outboxNavRows";
import { isSnoozedNavRow } from "@/lib/snooze/snoozeNavFolder";
import { readAccountSettings } from "@/lib/mail/accountSettingsPrefs";
import { getAccountDisplayLabel } from "@/lib/mail/accountDisplayLabel";
import {
  AURELIA_UNIFIED_INBOX_FOLDER,
  isUnifiedInboxNavFolder,
  showUnifiedInboxEntry,
  UNIFIED_INBOX_LABEL,
} from "@/lib/unified/constants";
import type { MailAccountSummary, MailFolder } from "@/lib/types/mail";
import { UnreadCountBadge } from "@/features/inbox/UnreadCountBadge";
import { cn } from "@/lib/cn";

type Props = {
  accounts: MailAccountSummary[];
  activeAccountId: number;
  expandedAccountIds: ReadonlySet<number>;
  selectedFolder: string;
  getAccountFolders: (accountId: number) => MailFolder[];
  isAccountFoldersLoading?: (accountId: number) => boolean;
  getAccountOutboxState: (accountId: number) => AccountOutboxState;
  getAccountSnoozeCount?: (accountId: number) => number;
  /** Compte dont un brouillon est en cours d’enregistrement IMAP. */
  draftSavePendingAccountId?: number | null;
  onToggleAccount: (accountId: number) => void;
  onSelectFolder: (accountId: number, folder: string) => void;
  onOpenSettings: (accountId: number) => void;
  onCreateFolder?: (accountId: number) => void;
  onRenameFolder?: (accountId: number, folder: MailFolder) => void;
  onDeleteFolder?: (accountId: number, folder: MailFolder) => void;
  onMoveAccount?: (accountId: number, direction: "up" | "down") => void;
  unifiedUnreadCount?: number;
  /** Force le rechargement des libellés après sauvegarde paramètres. */
  settingsVersion?: number;
  /** Séparation déjà rendue sous Rédiger — éviter la double ligne. */
  omitUnifiedTopRule?: boolean;
};

/** Pastille INBOX — visible uniquement si l’accordéon compte est fermé. */
function AccountCollapsedUnreadBadge({
  accountId,
  folders,
}: {
  accountId: number;
  folders: MailFolder[];
}) {
  const withBadges = useFoldersWithSnapshotUnread(accountId, folders);
  const [holdRev, setHoldRev] = useState(0);
  useEffect(() => subscribeUnreadHold(() => setHoldRev((v) => v + 1)), []);
  void holdRev;
  const unread = Math.max(
    0,
    sumInboxUnreadFromFolders(withBadges) - getUnreadHold(accountId)
  );
  return <UnreadCountBadge count={unread} variant="solid" />;
}

function FolderRow({
  folder,
  selected,
  indent = 0,
  extraCount = 0,
  badgeOverride,
  manageable = false,
  onSelect,
  onOpenMenu,
}: {
  folder: MailFolder;
  selected: boolean;
  indent?: number;
  extraCount?: number;
  /** Pastille « En attente » : MySQL actif, pas les non-lus IMAP du dossier hold. */
  badgeOverride?: number;
  manageable?: boolean;
  onSelect: () => void;
  onOpenMenu?: (x: number, y: number) => void;
}) {
  const Icon = folderIconFor(folder.label, folder.name);
  const unread = badgeOverride ?? (folder.unread ?? 0) + extraCount;

  return (
    <div
      className={cn(
        "group/folder flex min-w-0 items-center gap-0.5 rounded-lg pr-0.5 transition",
        selected ? "bg-[var(--mail-accent-soft)]" : "hover:bg-[var(--mail-surface-hover)]"
      )}
      onContextMenu={
        manageable && onOpenMenu
          ? (e) => {
              e.preventDefault();
              onOpenMenu(e.clientX, e.clientY);
            }
          : undefined
      }
    >
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          "flex min-w-0 flex-1 items-center gap-1.5 rounded-lg py-1.5 pr-1 text-left text-xs transition",
          selected ? "font-medium text-[var(--mail-accent)]" : "text-[var(--mail-text)]"
        )}
        style={{ paddingLeft: `${0.5 + indent * 0.625}rem` }}
      >
        <Icon
          className={cn("h-3.5 w-3.5 shrink-0", selected ? "text-[var(--mail-accent)]" : "text-[var(--mail-muted)]")}
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate">{folder.label}</span>
        <UnreadCountBadge count={unread} variant="soft" />
      </button>
      {manageable && onOpenMenu ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            const rect = e.currentTarget.getBoundingClientRect();
            onOpenMenu(rect.right, rect.bottom);
          }}
          className="shrink-0 rounded-md p-1 text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface)] hover:text-[var(--mail-text)] focus:opacity-100 focus-visible:opacity-100 opacity-70 group-hover/folder:opacity-100"
          aria-label={`Actions pour ${folder.label}`}
        >
          <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

function AccountFolders({
  accountId,
  activeAccountId,
  folders,
  selectedFolder,
  foldersLoading,
  getAccountOutboxState,
  getAccountSnoozeCount,
  draftSavePending = false,
  onSelectFolder,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
}: {
  accountId: number;
  activeAccountId: number;
  folders: MailFolder[];
  selectedFolder: string;
  foldersLoading?: boolean;
  getAccountOutboxState: (accountId: number) => AccountOutboxState;
  getAccountSnoozeCount?: (accountId: number) => number;
  draftSavePending?: boolean;
  onSelectFolder: (accountId: number, folder: string) => void;
  onCreateFolder?: (accountId: number) => void;
  onRenameFolder?: (accountId: number, folder: MailFolder) => void;
  onDeleteFolder?: (accountId: number, folder: MailFolder) => void;
}) {
  const [othersOpen, setOthersOpen] = useState(false);
  const [folderMenu, setFolderMenu] = useState<{
    folder: MailFolder;
    x: number;
    y: number;
  } | null>(null);
  const accountSettings = readAccountSettings(accountId);
  const foldersWithBadges = useFoldersWithSnapshotUnread(accountId, folders);
  const outboxState = getAccountOutboxState(accountId);
  const outboxTotal = accountOutboxTotalCount(outboxState);
  const accountSnoozeCount = getAccountSnoozeCount?.(accountId) ?? 0;
  const [holdRev, setHoldRev] = useState(0);
  useEffect(() => subscribeUnreadHold(() => setHoldRev((v) => v + 1)), []);
  void holdRev;

  const openFolderMenu = useCallback((folder: MailFolder, x: number, y: number) => {
    setFolderMenu({ folder, x, y });
  }, []);

  const canManageFolder = useCallback(
    (folder: MailFolder) =>
      isUserManagedImapFolder(folder.name, folder.label) &&
      (onRenameFolder != null || onDeleteFolder != null),
    [onDeleteFolder, onRenameFolder]
  );

  if (foldersLoading && folders.length === 0) {
    return <MailLoadingState variant="folders" layout="inline" className="py-1 pl-6" />;
  }

  const { primary, others } = buildNavLayoutFromSettings(foldersWithBadges, accountSettings);
  const inboxHold = getUnreadHold(accountId);

  type NavRow = {
    key: string;
    folder: MailFolder;
    extraCount: number;
    badgeOverride?: number;
  };
  const navRows: NavRow[] = primary.map((folder) => {
    const isOutboxRow = isOutboxNavFolder(folder.name);
    const snoozedRow = isSnoozedNavRow(folder);
    const showSnoozeBadge = snoozedRow && accountSnoozeCount > 0;
    const isInbox = folder.name.toUpperCase() === "INBOX";
    const inboxUnread =
      isInbox && inboxHold > 0
        ? Math.max(0, (folder.unread ?? 0) - inboxHold)
        : undefined;
    return {
      key: folder.name,
      folder: isOutboxRow
        ? { ...folder, label: resolveOutboxNavLabel(outboxState) }
        : draftSavePending && isDraftsNavFolder(folder.name, folder.label)
          ? { ...folder, label: DRAFTS_SAVING_FOLDER_LABEL }
          : folder,
      extraCount: 0,
      badgeOverride: isOutboxRow
        ? outboxTotal > 0
          ? outboxTotal
          : undefined
        : showSnoozeBadge
          ? accountSnoozeCount
          : inboxUnread != null
            ? inboxUnread
            : undefined,
    };
  });

  if (navRows.length === 0 && others.length === 0) {
    return (
      <p className="py-2 pl-6 text-[10px] text-[var(--mail-muted)]">
        Aucun dossier affiché — ouvrez les paramètres du compte.
      </p>
    );
  }

  return (
    <div className="space-y-0.5 border-t border-[var(--mail-border-soft)] px-2 pb-2 pt-2">
      {navRows.map((row) => {
        const manageable = canManageFolder(row.folder);
        return (
          <FolderRow
            key={row.key}
            folder={row.folder}
            selected={accountId === activeAccountId && selectedFolder === row.folder.name}
            extraCount={row.extraCount}
            badgeOverride={row.badgeOverride}
            manageable={manageable}
            onSelect={() => onSelectFolder(accountId, row.folder.name)}
            onOpenMenu={
              manageable ? (x, y) => openFolderMenu(row.folder, x, y) : undefined
            }
          />
        );
      })}

      {others.length > 0 ? (
        <div className="pt-0.5">
          <button
            type="button"
            onClick={() => setOthersOpen((v) => !v)}
            className="flex w-full items-center gap-1 rounded-lg py-1.5 pl-2 pr-2 text-left text-[11px] font-medium text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)]"
          >
            {othersOpen ? (
              <ChevronDown className="h-3 w-3 shrink-0" aria-hidden />
            ) : (
              <ChevronRight className="h-3 w-3 shrink-0" aria-hidden />
            )}
            Tous les dossiers
            <span className="text-[10px] font-normal tabular-nums">({others.length})</span>
          </button>
          {onCreateFolder ? (
            <button
              type="button"
              onClick={() => onCreateFolder(accountId)}
              className="mt-0.5 flex w-full items-center gap-1.5 rounded-lg py-1.5 pl-5 pr-2 text-left text-[11px] font-medium text-[var(--mail-accent)] transition hover:bg-[var(--mail-surface-hover)]"
            >
              <FolderPlus className="h-3 w-3 shrink-0" aria-hidden />
              Nouveau dossier
            </button>
          ) : null}
          {othersOpen
            ? others.map((folder) => {
                const manageable = canManageFolder(folder);
                return (
                  <FolderRow
                    key={folder.name}
                    folder={folder}
                    selected={accountId === activeAccountId && selectedFolder === folder.name}
                    indent={1}
                    manageable={manageable}
                    onSelect={() => onSelectFolder(accountId, folder.name)}
                    onOpenMenu={
                      manageable
                        ? (x, y) => openFolderMenu(folder, x, y)
                        : undefined
                    }
                  />
                );
              })
            : null}
        </div>
      ) : onCreateFolder ? (
        <button
          type="button"
          onClick={() => onCreateFolder(accountId)}
          className="mt-0.5 flex w-full items-center gap-1.5 rounded-lg py-1.5 pl-2 pr-2 text-left text-[11px] font-medium text-[var(--mail-accent)] transition hover:bg-[var(--mail-surface-hover)]"
        >
          <FolderPlus className="h-3 w-3 shrink-0" aria-hidden />
          Nouveau dossier
        </button>
      ) : null}
      <MailContextMenu
        open={folderMenu != null}
        x={folderMenu?.x ?? 0}
        y={folderMenu?.y ?? 0}
        items={[
          ...(onRenameFolder
            ? [{ id: "rename", label: "Renommer", icon: Pencil }]
            : []),
          ...(onDeleteFolder
            ? [{ id: "delete", label: "Supprimer", icon: Trash2, danger: true }]
            : []),
        ]}
        onAction={(actionId) => {
          if (!folderMenu) return;
          if (actionId === "rename") onRenameFolder?.(accountId, folderMenu.folder);
          if (actionId === "delete") onDeleteFolder?.(accountId, folderMenu.folder);
        }}
        onClose={() => setFolderMenu(null)}
      />
    </div>
  );
}

export function MailAccountAccordion({
  accounts,
  activeAccountId,
  expandedAccountIds,
  selectedFolder,
  getAccountFolders,
  isAccountFoldersLoading,
  getAccountOutboxState,
  getAccountSnoozeCount,
  draftSavePendingAccountId = null,
  onToggleAccount,
  onSelectFolder,
  onOpenSettings,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onMoveAccount,
  unifiedUnreadCount = 0,
  settingsVersion = 0,
  omitUnifiedTopRule = false,
}: Props) {
  void settingsVersion;
  const canReorder = accounts.length > 1 && onMoveAccount != null;
  const unifiedSelected = isUnifiedInboxNavFolder(selectedFolder);
  const showUnified = showUnifiedInboxEntry(accounts.length);

  const [accountMenu, setAccountMenu] = useState<{
    accountId: number;
    x: number;
    y: number;
  } | null>(null);

  return (
    <nav className="shrink-0 px-2 pb-2 pt-0" aria-label="Comptes et dossiers">
      {showUnified ? (
        <div
          className={cn(
            "mail-nav-unified mb-2 min-w-0 overflow-hidden border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] shadow-sm transition",
            "rounded-none rounded-b-xl border-t-0",
            unifiedSelected
              ? "border-[var(--mail-accent)]/40 bg-[var(--mail-accent-soft)] ring-1 ring-[var(--mail-accent)]/20 ring-inset"
              : "hover:border-[var(--mail-border)] hover:bg-[var(--mail-surface-hover)]"
          )}
        >
          {omitUnifiedTopRule ? null : (
            <div
              className="mail-nav-unified__rule h-px w-full bg-[var(--mail-border)]"
              aria-hidden
            />
          )}
          <button
            type="button"
            onClick={() =>
              onSelectFolder(accounts[0]?.id ?? activeAccountId, AURELIA_UNIFIED_INBOX_FOLDER)
            }
            className={cn(
              "relative flex w-full min-w-0 items-center justify-center gap-2 px-3 py-3 text-sm transition",
              unifiedSelected
                ? "font-medium text-[var(--mail-accent)]"
                : "text-[var(--mail-text)]"
            )}
          >
            <Inbox className="h-4 w-4 shrink-0" strokeWidth={2} aria-hidden />
            <span className="truncate">{UNIFIED_INBOX_LABEL}</span>
            {unifiedUnreadCount > 0 ? (
              <UnreadCountBadge
                count={unifiedUnreadCount}
                variant="solid"
                className="absolute right-2 top-1/2 -translate-y-1/2"
              />
            ) : null}
          </button>
        </div>
      ) : null}

      <div className="space-y-2">
        {accounts.map((account, index) => {
          const expanded = expandedAccountIds.has(account.id);
          const isActive = account.id === activeAccountId;
          const accountFolders = getAccountFolders(account.id);
          const accountFoldersLoading = isAccountFoldersLoading?.(account.id) ?? false;
          const displayLabel = getAccountDisplayLabel(account);

          return (
            <div
              key={account.id}
              className={cn(
                "min-w-0 overflow-hidden rounded-xl border bg-[var(--mail-surface)] shadow-sm transition",
                isActive
                  ? "border-[var(--mail-accent)]/35 ring-1 ring-[var(--mail-accent)]/15"
                  : "border-[var(--mail-border-soft)] hover:border-[var(--mail-border)]"
              )}
            >
              <div className="flex min-w-0 items-stretch gap-0.5 p-1">
                {canReorder ? (
                  <div className="flex shrink-0 flex-col justify-center gap-0.5 px-0.5">
                    <button
                      type="button"
                      disabled={index === 0}
                      onClick={() => onMoveAccount!(account.id, "up")}
                      className="rounded p-0.5 text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)] disabled:opacity-25"
                      aria-label={`Monter ${displayLabel}`}
                    >
                      <ChevronUp className="h-3 w-3" aria-hidden />
                    </button>
                    <button
                      type="button"
                      disabled={index === accounts.length - 1}
                      onClick={() => onMoveAccount!(account.id, "down")}
                      className="rounded p-0.5 text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)] disabled:opacity-25"
                      aria-label={`Descendre ${displayLabel}`}
                    >
                      <ChevronDown className="h-3 w-3" aria-hidden />
                    </button>
                  </div>
                ) : null}

                <button
                  type="button"
                  onClick={() => onToggleAccount(account.id)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setAccountMenu({
                      accountId: account.id,
                      x: e.clientX,
                      y: e.clientY,
                    });
                  }}
                  className="flex min-w-0 flex-1 items-center gap-2 px-1.5 py-2 text-left"
                  title={displayLabel}
                >
                  <AccountAvatar account={account} size="sm" />
                  {expanded ? (
                    <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[var(--mail-muted)]" aria-hidden />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[var(--mail-muted)]" aria-hidden />
                  )}
                  <span
                    className={cn(
                      "min-w-0 flex-1 truncate text-sm",
                      isActive
                        ? "font-semibold text-[var(--mail-text)]"
                        : "font-medium text-[var(--mail-text)]"
                    )}
                  >
                    {displayLabel}
                  </span>
                  {!expanded ? (
                    <AccountCollapsedUnreadBadge
                      accountId={account.id}
                      folders={accountFolders}
                    />
                  ) : null}
                </button>

                {expanded ? (
                  <button
                    type="button"
                    onClick={() => onOpenSettings(account.id)}
                    className="shrink-0 self-center rounded-lg p-2 text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)]"
                    aria-label={`Paramètres de ${displayLabel}`}
                  >
                    <Settings className="h-3.5 w-3.5" aria-hidden />
                  </button>
                ) : null}
              </div>

              {expanded ? (
                <AccountFolders
                  accountId={account.id}
                  activeAccountId={activeAccountId}
                  folders={accountFolders}
                  selectedFolder={selectedFolder}
                  foldersLoading={accountFoldersLoading}
                  getAccountOutboxState={getAccountOutboxState}
                  getAccountSnoozeCount={getAccountSnoozeCount}
                  draftSavePending={draftSavePendingAccountId === account.id}
                  onSelectFolder={onSelectFolder}
                  onCreateFolder={onCreateFolder}
                  onRenameFolder={onRenameFolder}
                  onDeleteFolder={onDeleteFolder}
                />
              ) : null}
            </div>
          );
        })}
      </div>

      <MailContextMenu
        open={accountMenu != null}
        x={accountMenu?.x ?? 0}
        y={accountMenu?.y ?? 0}
        items={[{ id: "settings", label: "Paramètres du compte", icon: Settings }]}
        onClose={() => setAccountMenu(null)}
        onAction={(id) => {
          if (id === "settings" && accountMenu) {
            onOpenSettings(accountMenu.accountId);
          }
          setAccountMenu(null);
        }}
      />
    </nav>
  );
}
